import {
  Button,
  Chip,
  CircularProgress,
  Divider,
  Popover,
  Stack,
  SvgIcon,
  Tooltip,
  Typography,
} from '@mui/material'
import { useCallback, useMemo, useState } from 'react'
import * as R from 'remeda'
import { DetailDrawer } from '~/features/models/models-list/components/DetailDrawer'
import { useDataGridProps } from '~/ui/hooks/useDataGridProps'
import { ImportModels } from '~/features/models/models-list/components/ImportModels'
import { ModelListRowModel } from '~/features/models/models-list/types'
import { CustomDataGrid } from '~/ui/components/CustomDataGrid'
import { SelectionPanel, SelectionPanelAction } from '~/ui/components/SelectionPanel'
import { useCreateShopifyProducts } from '~/features/shopify/hooks/useCreateShopifyProducts'
import { generatePhotoUrl } from '~/utils/shared'
import { useGetShopifyProducts } from '~/features/shopify/hooks/useGetShopifyProducts'
import { useGetInventory } from '~/features/hooks/useGetInventory'
import { SHOPIFY_LOCATION_ID, shopifyCategoryMap } from '~/features/shopify/utils'
import { checkForIssues } from './checkForIssues'
import { IconChevronDown, IconPlus } from '@tabler/icons-react'
import { useSetShopifyQuantity } from '~/features/shopify/hooks/useSetShopifyQuantity'
import { toast } from 'sonner'
import { useBucketPhotos } from './hooks/useBucketPhotos'
import { extractImageIndexFromPath } from '../utils'
import { useGetModels } from '~/features/hooks/useGetModels'
import { UpdatePrices } from './actions/UpdatePrices'
import { useUpdateShopifyVariants } from './hooks/useUpdateShopifyVariants'
import { useDeleteFromShopify } from './hooks/useDeleteFromShopify'
import { FilterCondition, useModelListColumns } from './useModelListColumns'
import { SimpleDialog } from '~/ui/components/SimpleDialog'

const warningClassName = 'has-issues'

export const ModelsList = () => {
  const [drawerAnchorId, setDrawerAnchorId] = useState<number | null>(null)
  const handleCloseDrawer = () => {
    setDrawerAnchorId(null)
  }

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)

  const [filters, setFilters] = useState<Array<FilterCondition>>([])
  const [filterComponent, setFilterComponent] = useState<{
    Component: React.ReactNode
    anchorEl: HTMLDivElement | null
  } | null>(null)

  const {
    data: inventoryData,
    isFetching: isLoadingInventory,
    isLoading: isInitiallyLoadingInventory,
  } = useGetInventory()

  const {
    data: shopifyProducts,
    isLoading: isInitiallyLoadingShopifyProducts,
    isFetching: isLoadingShopifyProducts,
  } = useGetShopifyProducts()

  const { mutate: createShopifyProducts, isPending: isCreatingShopifyProducts } =
    useCreateShopifyProducts()

  const { mutate: setShopifyQuantity, isPending: isSettingShopifyQuantity } =
    useSetShopifyQuantity()

  const { mutate: updateShopifyVariants, isPending: isUpdatingShopifyVariants } =
    useUpdateShopifyVariants()

  const { mutate: deleteFromShopify, isPending: isDeletingFromShopify } = useDeleteFromShopify()

  const { data: models, isLoading: isLoadingModels } = useGetModels()

  const { data: bucketPhotos, isLoading: isLoadingBucketPhotos } = useBucketPhotos()

  const isInitiallyLoading =
    isInitiallyLoadingInventory || isInitiallyLoadingShopifyProducts || isLoadingBucketPhotos
  const isMutating =
    isCreatingShopifyProducts ||
    isSettingShopifyQuantity ||
    isUpdatingShopifyVariants ||
    isDeletingFromShopify
  const isLoading =
    isLoadingInventory || isLoadingShopifyProducts || isLoadingBucketPhotos || isLoadingModels

  const hideFilterComponent = useCallback(() => {
    setFilterComponent((prev) => (prev ? { ...prev, anchorEl: null } : prev))
  }, [])

  const columns = useModelListColumns({
    filters,
    setFilters,
    isInitiallyLoading,
    hideFilterComponent,
    setDrawerAnchorId,
  })

  const bucketPhotoMap = useMemo(() => {
    if (!bucketPhotos) return {}
    return R.pipe(
      bucketPhotos,
      R.flatMap((photo) => {
        const [supplierCode, material, fileName] = photo.name?.split('/') ?? []
        if (!supplierCode || !material || !fileName) return []

        return [
          {
            supplierCode,
            material,
            fileName,
          },
        ]
      }),
      R.groupBy((photo) => photo.supplierCode),
      R.mapValues((photos) => R.groupBy(photos, (photo) => photo.material))
    )
  }, [bucketPhotos])

  const rows = useMemo<ModelListRowModel[]>(() => {
    if (!inventoryData || !models) return []

    const shopifyProductMap = R.mapToObj(shopifyProducts ?? [], (product) => [
      product.title,
      product,
    ])

    const inventoryMap = R.mapToObj(inventoryData, (inv) => [
      inv.models_v2.stock_code,
      inv.models_v2,
    ])
    const inventoryGroup = R.groupBy(inventoryData, (inv) => inv.models_v2.stock_code)

    const validatedRows = R.values(inventoryMap).map((data) => {
      const inventory = inventoryGroup[data.stock_code] ?? []
      const shopifyProduct = shopifyProductMap[data.stock_code]
      const images = bucketPhotoMap[data.supplier_code]?.[data.material]

      return {
        ...data,
        images,
        shopifyProduct,
        shopifyQuantity: R.sumBy(shopifyProduct?.variants.nodes ?? [], (n) => n.inventoryQuantity),
        warnings: checkForIssues({
          images,
          model: data,
          shopifyProductMap,
          inventory: inventory.filter((inv) => inv.status === 'available'),
        }),
        inventory,
        availableInventory: inventory.filter((inv) => inv.status === 'available').length,
        soldInventory: inventory.filter((inv) => inv.status === 'sold').length,
        supplier: data.suppliers,
      }
    })

    const missingModels = models.filter((model) => !inventoryMap[model.stock_code])

    const allRows: ModelListRowModel[] = validatedRows.concat(
      missingModels.map((model) => {
        const shopifyProduct = shopifyProductMap[model.stock_code]
        const images = bucketPhotoMap[model.supplier_code]?.[model.material]
        return {
          ...model,
          shopifyProduct,
          warnings: [
            { message: 'Model not found in inventory', key: 'model_missing_in_inventory' },
          ],
          images,
          availableInventory: 0,
          soldInventory: 0,
          shopifyQuantity: 0,
          inventory: [],
          supplier: model.suppliers,
        }
      })
    )

    return filters.reduce((acc, filter) => filter.setter(acc), allRows)
  }, [bucketPhotoMap, inventoryData, models, shopifyProducts, filters])

  const drawerAnchor = useMemo<ModelListRowModel | null>(() => {
    return rows.find((row) => row.id === drawerAnchorId) ?? null
  }, [drawerAnchorId, rows])

  const { selectedRows, exportDatagrid, onRowSelectionModelChange, selectionModel, reset } =
    useDataGridProps<ModelListRowModel>({
      rows,
    })

  const actions: SelectionPanelAction[] = useMemo(
    () => [
      {
        key: 'enable-in-eshop',
        Component: () => (
          <Button
            size="small"
            onClick={() => {
              createShopifyProducts(
                R.pipe(
                  selectedRows,
                  R.map((row) => {
                    const media = row.images
                      ? R.pipe(
                          row.images,

                          R.map((data) => {
                            const index = extractImageIndexFromPath(data.fileName)
                            return index
                              ? {
                                  index,
                                  originalSource:
                                    generatePhotoUrl({
                                      supplierCode: data.supplierCode,
                                      material: data.material,
                                      fileName: data.fileName,
                                      size: 'small',
                                    }) ?? '',
                                  alt: row.stock_code,
                                  mediaContentType: 'IMAGE' as const,
                                }
                              : null
                          }),
                          R.filter(R.isNonNull),
                          R.sortBy((data) => data.index)
                        )
                      : null
                    const mainImage = media?.[0]
                    if (
                      !media ||
                      media.length === 0 ||
                      media.length !== row.images?.length ||
                      !mainImage
                    ) {
                      toast.error(`Inconsistent images for : '${row.stock_code}'`, {
                        id: 'inconsistent-images',
                      })
                      return null
                    }
                    return {
                      inventory: row.inventory,
                      productType: row.product_type,
                      price: row.price,
                      product: {
                        title: row.stock_code,
                        handle: row.stock_code,
                        status: 'ACTIVE' as const,
                        productType: row.material,
                        category: shopifyCategoryMap[row.product_type],
                        metafields: [
                          {
                            namespace: 'supabase',
                            type: 'integer' as const,
                            value: row.id.toString(),
                            key: 'id',
                          },
                        ],
                      },
                      media: [mainImage],
                    }
                  }),
                  R.filter(R.isNonNull)
                )
              )
            }}
            disabled={
              isMutating ||
              isLoading ||
              selectedRows.some(
                (row) =>
                  row.status_v2 !== 'ready' || !!row.warnings?.length || !!row.shopify_product_id
              )
            }
            className={isMutating || isLoading ? 'loading' : ''}
            endIcon={isCreatingShopifyProducts ? <CircularProgress size={16} /> : null}
          >
            Create in Shopify
          </Button>
        ),
      },
      {
        key: 'set-shopify-quantity',
        Component: () => (
          <Button
            size="small"
            onClick={() => {
              const invalidInventory = selectedRows.some((row) =>
                row.inventory.some((inv) => inv.status === 'available' && !inv.inventory_item_id)
              )

              if (invalidInventory) {
                return toast.warning('Missing inventory item id')
              }

              setShopifyQuantity({
                name: 'available',
                reason: 'correction',
                quantities: selectedRows
                  .map((row) => {
                    const availableInventory = row.inventory.filter(
                      (inv) => inv.status === 'available'
                    )

                    const grouped = R.groupBy(
                      availableInventory,
                      (inv) => inv.inventory_item_id ?? 'unknown'
                    )

                    // Get all variants from Shopify product to ensure we set quantity to 0 for sold-out variants
                    const allVariants = row.shopifyProduct?.variants.nodes ?? []

                    return allVariants.flatMap((variant) => {
                      const inventoryItemId = variant.inventoryItem.id
                      const groupedInventory = grouped[inventoryItemId] ?? []
                      const quantity = groupedInventory.length
                      const compareQuantity = variant.inventoryQuantity

                      return {
                        inventoryItemId,
                        quantity,
                        compareQuantity,
                        locationId: SHOPIFY_LOCATION_ID,
                      } as const
                    })
                  })
                  .flat(),
              })
            }}
            disabled={
              isMutating ||
              isLoading ||
              !selectedRows.every(
                (row) =>
                  row.shopifyProduct &&
                  row.warnings?.some((w) => w.key === 'inconsistent_shopify_quantity')
              )
            }
            className={isMutating || isLoading ? 'loading' : ''}
            endIcon={isSettingShopifyQuantity ? <CircularProgress size={16} /> : null}
          >
            Match Shopify quantity
          </Button>
        ),
      },
      {
        key: 'match-shopify-prices',
        Component: () => (
          <Button
            size="small"
            onClick={() => updateShopifyVariants(selectedRows)}
            disabled={
              isMutating ||
              isLoading ||
              selectedRows.length === 0 ||
              !selectedRows.every(
                (row) =>
                  row.shopifyProduct &&
                  row.warnings?.some((w) => w.key === 'inconsistent_shopify_price')
              )
            }
            className={isMutating || isLoading ? 'loading' : ''}
            endIcon={isUpdatingShopifyVariants ? <CircularProgress size={16} /> : null}
          >
            Match Shopify prices
          </Button>
        ),
      },
      {
        key: 'update-prices',
        Component: () => (
          <UpdatePrices
            selectedRows={selectedRows}
            disabled={isMutating || isLoading}
            className={isMutating || isLoading ? 'loading' : ''}
          />
        ),
      },
      {
        key: 'remove-from-shopify',
        Component: () => (
          <Button
            size="small"
            color="error"
            onClick={() => setDeleteDialogOpen(true)}
            disabled={
              isMutating ||
              isLoading ||
              selectedRows.length === 0 ||
              !selectedRows.every((row) => !!row.shopify_product_id)
            }
            endIcon={isDeletingFromShopify ? <CircularProgress size={16} /> : null}
            className={isMutating || isLoading ? 'loading' : ''}
          >
            Remove from Shopify
          </Button>
        ),
      },
    ],
    [
      isMutating,
      isLoading,
      selectedRows,
      isCreatingShopifyProducts,
      createShopifyProducts,
      setShopifyQuantity,
      isSettingShopifyQuantity,
      updateShopifyVariants,
      isUpdatingShopifyVariants,
      isDeletingFromShopify,
      setDeleteDialogOpen,
    ]
  )

  return (
    <Stack flex={1} gap={2}>
      <DetailDrawer open={!!drawerAnchor} onClose={handleCloseDrawer} data={drawerAnchor} />
      <SimpleDialog
        open={deleteDialogOpen}
        title="Remove from Shopify"
        text={`Remove ${selectedRows.length} product${selectedRows.length !== 1 ? 's' : ''} from Shopify?`}
        description={`This will delete the Shopify listing and reset the model status back to 'ready'.\n\nProducts: ${selectedRows.map((r) => r.stock_code).join(', ')}`}
        color="error"
        confirmText="Remove"
        isLoading={isDeletingFromShopify}
        onClose={() => setDeleteDialogOpen(false)}
        onSubmit={() => {
          deleteFromShopify(selectedRows, {
            onSettled: () => setDeleteDialogOpen(false),
          })
        }}
      />
      <Stack direction="row" justifyContent="space-between">
        <Typography variant="h6">Models List</Typography>

        <Stack direction="row" gap={1}>
          <Button variant="outlined" disabled={selectedRows.length === 0} onClick={exportDatagrid}>
            Export
          </Button>
          <ImportModels />
        </Stack>
      </Stack>

      <Popover
        open={!!filterComponent?.anchorEl}
        onClose={hideFilterComponent}
        anchorEl={filterComponent?.anchorEl}
        slotProps={{
          transition: { onExited: () => setFilterComponent(null) },
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        {filterComponent?.Component}
      </Popover>
      <Stack direction="row" spacing={1} useFlexGap>
        {columns
          .flatMap((c) => ('customFilter' in c ? [c] : []))
          .map((column) => {
            const existingFilter = filters.find((f) => f.id === column.field)
            return (
              <Tooltip
                key={column.field}
                title={existingFilter ? existingFilter.label : undefined}
                placement="top"
              >
                <Chip
                  label={
                    <Stack
                      divider={
                        <Divider
                          orientation="vertical"
                          flexItem
                          sx={{ height: 14, alignSelf: 'center' }}
                        />
                      }
                      direction="row"
                      spacing={1}
                    >
                      <Typography variant="caption">{column.headerName}</Typography>
                      {existingFilter?.label ? (
                        <Typography
                          variant="caption"
                          sx={{
                            whiteSpace: 'nowrap',
                            textOverflow: 'ellipsis',
                            overflow: 'hidden',
                            maxWidth: 100,
                            fontWeight: 'bold',
                          }}
                        >
                          {existingFilter.label}
                        </Typography>
                      ) : null}
                    </Stack>
                  }
                  size="small"
                  onDelete={
                    existingFilter
                      ? () => setFilters(filters.filter((f) => f.id !== existingFilter.id))
                      : undefined
                  }
                  icon={
                    existingFilter ? (
                      <SvgIcon sx={{ width: 14, height: 14 }}>
                        <IconChevronDown />
                      </SvgIcon>
                    ) : (
                      <SvgIcon sx={{ width: 14, height: 14 }}>
                        <IconPlus />
                      </SvgIcon>
                    )
                  }
                  onClick={(e) => {
                    setFilterComponent({
                      Component: column.customFilter?.Component,
                      anchorEl: e.currentTarget,
                    })
                  }}
                />
              </Tooltip>
            )
          })}
      </Stack>
      <Stack position="relative" flex={1} height={1}>
        <Stack flex={1} sx={{ gap: 1, height: 1 }}>
          <CustomDataGrid
            rows={rows}
            columns={columns}
            rowSelectionModel={selectionModel}
            onRowSelectionModelChange={onRowSelectionModelChange}
            disableRowSelectionOnClick
            checkboxSelection
            loading={isLoading}
            disableColumnMenu
            getRowClassName={(params) => {
              return params.row.warnings?.length ? warningClassName : ''
            }}
            sx={{
              [`& .${warningClassName}`]: {
                borderLeft: (t) => `2px solid ${t.palette.warning.light}`,
              },
            }}
          >
            <SelectionPanel rows={selectedRows} actions={actions} onClose={reset} />
          </CustomDataGrid>
        </Stack>
      </Stack>
    </Stack>
  )
}
